import { useMemo, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Separator } from "@volca/ui/components/ui/separator";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import { useGetBusinessTechnicianJobCounts } from "@/business/query";
import { useBusiness } from "@/core/business";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import { createDefaults } from "@/core/utils/loading";
import { useGetUsersWithStats } from "@/user/query";
import { DetailedUserWithStatsAndJobCount } from "@/user/user";
import { isAdmin } from "@/user/utils";
import { InfoPair } from "./info-pair";
import { LegendLabel } from "./legend-label";
import { ParticipationTrackerListItem } from "./participation-tracker-list-item";
import { ParticipationTrackerListItemSkeleton } from "./participation-tracker-list-item-skeleton";

const LOADING_USERS = createDefaults("loading-user", 5);
const VISIBLE_ROWS = 8;

interface ParticipationTrackerListContentProps {
  isLoading: boolean;
  isError: boolean;
  sortedUsers: DetailedUserWithStatsAndJobCount[];
  maxValue: number;
  showCompletedJobs: boolean;
}

const ParticipationTrackerListContent = ({
  isLoading,
  isError,
  sortedUsers,
  maxValue,
  showCompletedJobs,
}: ParticipationTrackerListContentProps) => {
  const [showAll, setShowAll] = useState(false);

  if (isLoading) {
    return LOADING_USERS.map((user, index) => (
      <ParticipationTrackerListItemSkeleton
        key={user.id}
        widthPercentage={100 - index * 20}
      />
    ));
  }
  if (isError || sortedUsers.length === 0) {
    return (
      <Empty className={emptyStateVariants({ layout: "inline" })}>
        <EmptyHeader>
          <EmptyDescription>
            {isError
              ? "Couldn't load your team. Please try again."
              : "No team members yet."}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }
  const visibleUsers = showAll
    ? sortedUsers
    : sortedUsers.slice(0, VISIBLE_ROWS);
  return (
    <>
      {visibleUsers.map((user) => (
        <ParticipationTrackerListItem
          key={user.id}
          user={user}
          maxValue={maxValue}
          showCompletedJobs={showCompletedJobs}
        />
      ))}
      {sortedUsers.length > VISIBLE_ROWS && (
        <Button
          variant="ghost"
          size="sm"
          className="my-2 self-start"
          aria-expanded={showAll}
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? "Show fewer" : `Show all ${sortedUsers.length}`}
        </Button>
      )}
    </>
  );
};

/** `className` sets the card's width in the dashboard's row. */
const ParticipationTrackerPane = ({ className }: { className?: string }) => {
  const { businessId, selectedBusiness } = useBusiness();
  const { enabled: showCompletedJobsOnDashboard } = useFeatureFlag(
    FEATURE_FLAG_NAMES.show_completed_jobs_on_dashboard,
  );
  const serviceTitanEnabled = selectedBusiness?.service_titan_enabled;

  const showCompletedJobs = showCompletedJobsOnDashboard && serviceTitanEnabled;

  const {
    data: technicianJobCounts = {},
    isLoading: isLoadingTechnicianJobCounts,
  } = useGetBusinessTechnicianJobCounts(businessId, {
    enabled: showCompletedJobs,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const {
    data: users = [],
    isLoading,
    isError,
  } = useGetUsersWithStats(businessId, "last_thirty_days");

  const usersWithoutAdminsWithCounts = useMemo(
    () =>
      users
        .filter((user) => !isAdmin(user.role))
        .map((user) => ({
          ...user,
          job_count: technicianJobCounts[user.id]?.job_count ?? 0,
        })),
    [users, technicianJobCounts],
  );

  const sortedUsers = useMemo(
    () =>
      [...usersWithoutAdminsWithCounts].sort(
        (a, b) => (b.stats?.signup_count ?? 0) - (a.stats?.signup_count ?? 0),
      ),
    [usersWithoutAdminsWithCounts],
  );

  const totalJobsCompleted = useMemo(
    () => sortedUsers.reduce((acc, user) => acc + (user.job_count ?? 0), 0),
    [sortedUsers],
  );

  const maxValue = useMemo(() => {
    if (sortedUsers.length === 0) return 0;

    const maxSignups = Math.max(
      ...sortedUsers.map((user) => user.stats?.signup_count ?? 0),
    );
    const maxJobsCompleted = Math.max(
      ...sortedUsers.map((user) => user.job_count ?? 0),
    );

    return Math.max(maxSignups, maxJobsCompleted);
  }, [sortedUsers]);

  const totalSignups = useMemo(
    () =>
      sortedUsers.reduce(
        (acc, user) => acc + (user.stats?.signup_count ?? 0),
        0,
      ),
    [sortedUsers],
  );

  return (
    <Card className={cn("w-full gap-0 py-0", className)}>
      <Stack
        direction="row"
        align="start"
        justify="between"
        gap={2}
        className="w-full p-4"
      >
        <Stack align="start" gap={0}>
          <Text variant="title">Participation tracker</Text>
          <Text variant="caption" tone="muted">
            Last 30 days
          </Text>
        </Stack>
        <Stack
          align="start"
          direction="row"
          gap={1}
          className="flex-wrap justify-end"
        >
          <LegendLabel label="Sign-ups" type="signups" />
          {showCompletedJobs && (
            <LegendLabel label="Jobs completed" type="jobs_completed" />
          )}
        </Stack>
      </Stack>
      <Stack align="start" direction="row" gap={4} className="w-full px-4 pb-4">
        <InfoPair
          label="Total sign-ups"
          value={totalSignups.toString()}
          isLoading={isLoading}
        />
        {showCompletedJobs && (
          <InfoPair
            label="Total jobs"
            value={totalJobsCompleted.toString()}
            isLoading={isLoading || isLoadingTechnicianJobCounts}
          />
        )}
        <InfoPair
          label="Team size"
          value={sortedUsers.length.toString()}
          isLoading={isLoading}
        />
      </Stack>
      <Separator />
      <Stack align="start" gap={0} className="min-h-0 w-full flex-1 px-4">
        <ParticipationTrackerListContent
          isLoading={isLoading}
          isError={isError}
          sortedUsers={sortedUsers}
          maxValue={maxValue}
          showCompletedJobs={!!showCompletedJobs}
        />
      </Stack>
    </Card>
  );
};

export default ParticipationTrackerPane;
